import { randomUUID } from 'node:crypto';
import { isType, match } from 'variant';
import { generator } from '@wix/gena';
import { getTestOverrides } from '@wix/cli-test-overrides';
import type { IPackageManager } from '@wix/package-manager';
import { RepoType } from '@wix/package-manager';
import type { AppData } from '../../types.js';
import type { TemplateSource } from '../../parse-command-options.js';
import type { TemplateParams } from '../../validations/index.js';
import { fetchTemplate } from './template-fetcher.js';
import type { IdeForMcp } from '../../components/mcp/ConfigMcp.js';

interface GenerateAppOptions {
  packageName: string;
  packageFolder: string;
  packageManager: IPackageManager;
  repoType: RepoType;
  appData: AppData;
  template: TemplateSource;
  templateParams?: TemplateParams;
  ideForMcp?: IdeForMcp | void;
}

interface TemplateModelJSON {
  packageName: string;
  devCenter: { appId: string; appName: string };
  packageManager: { name: string; installCmd: string; runCmd: string };
  isMonorepo: boolean;
  deps: Record<string, string>;
  generatePageID: () => string;
  templateParams?: TemplateParams;
  ideForMcpType?: string;
}

const { wixCliDependencyVersion, wixCliAppDependencyVersion } =
  getTestOverrides();

export async function generateApp({
  packageName,
  packageFolder,
  appData,
  packageManager,
  repoType,
  template,
  templateParams,
  ideForMcp,
}: GenerateAppOptions): Promise<void> {
  const model: TemplateModelJSON = {
    packageName,
    devCenter: appData,
    deps: {
      '@wix/cli': wixCliDependencyVersion ?? '^1.0.0',
      '@wix/cli-app': wixCliAppDependencyVersion ?? '^1.0.0',
    },
    packageManager: {
      name: packageManager.name,
      installCmd: packageManager.getInstallCmd(),
      runCmd: packageManager.getRunCmd(),
    },
    isMonorepo: isType(repoType, RepoType.Monorepo),
    templateParams,
    generatePageID: () => randomUUID(),
    ideForMcpType: ideForMcp?.type,
  };

  const templateFolder = await match(template, {
    App: ({ payload: { cliCloneData } }) =>
      fetchTemplate(cliCloneData.repositoryUrl, cliCloneData.pathInRepository),
    GitRepo: ({ url, path }) => fetchTemplate(url, path),
    Local: ({ path }) => path,
    Default: ({ path }) => path,
  });

  await generator({
    template: templateFolder,
    cwd: packageFolder,
    data: model,
  });
}
